import { _ } from "../../../../../core/polyglot/polyglot.js";
import { formatAsMonthAndYear } from "../../../../../core/helpers/date-formatting-util.js";
import { spacer, stacker, text } from "../../../../../core/elements/index.js";
import AbstractForecastFiscalYearCardRenderingStrategy from "./AbstractForecastFiscalYearCardRenderingStrategy.js";

/**
 * RenderingStrategy to render the ForecastFiscalYearCard for the
 * Balance Sheet report tab.  This renders information, i.e. the end
 * of year forcasted values for equity, assets and liabilites - and
 * their expected growth from the current period.
 */
export default class BalanceSheetForecastFiscalYearCardRenderingStrategy extends AbstractForecastFiscalYearCardRenderingStrategy {
	constructor() {
		super(...arguments);

		this._timespan = undefined;
		this._openingEquityValue = undefined;
		this._closingEquityValue = undefined;
		this._equityPercentageGrowth = undefined;
	}

	renderContentAsBigCardOn(html) {
		html.div(
			{
				class: [this._cssClass(), "balance-sheet"],
				"data-test": "ForecastFiscalYearCard",
			},
			spacer(
				{ direction: "vertical" },
				html.div(
					{ class: "total" },
					this._renderTitleOn.bind(this),
					this._renderEquityOn.bind(this),
				),
				this._makeFooter(),
			),
		);
	}

	renderContentAsOneLinerOn(html) {
		html.render(
			stacker(
				{
					class: [this._cssClass(), "one-liner", "balance-sheet"],
					spacing: "between",
					dataTest: "ForecastFiscalYearCard",
				},
				html.div(this._renderTitleOn.bind(this), this._makeFooter()),
				stacker({ isBlock: true }, this._renderEquityOn.bind(this)),
			),
		);
	}

	setValues({
		timespan,
		openingEquityValue,
		closingEquityValue,
		equityPercentageGrowth,
	}) {
		super.setValues(...arguments);

		this._timespan = timespan;
		this._openingEquityValue = openingEquityValue;
		this._closingEquityValue = closingEquityValue;
		this._equityPercentageGrowth = equityPercentageGrowth;
	}

	_renderTitleOn(html) {
		html.div(
			{ "data-test": "title", class: "title" },
			_("{kpi} at year end", {
				kpi: text({ emphasize: true }, _("Equity")),
			}),
		);
	}

	_renderEquityOn(html) {
		html.div(
			{ class: "total" },
			html.span(
				{ class: "value", "data-test": "value" },
				html.strong(this._format(this._closingEquityValue)),
			),
		);
	}

	_makeFooter() {
		return stacker(
			{ class: "footer" },
			this._timespanRenderer(this._timespan),
			this._makeFormatedPercentage(this._equityPercentageGrowth),
		);
	}

	_makeFormatedPercentage(percentage) {
		if (isNaN(percentage)) return "";

		const isPositive = percentage >= 0;
		const growthString = `${isPositive ? "+" : "-"}${Math.abs(percentage)}%`;

		return (html) =>
			html.div(
				{
					class: [
						"value",
						{ positive: isPositive, negative: !isPositive },
					],
					"data-test": "growth",
					title: this._makePercentageInfo({ growthString }),
				},
				`(${growthString})`,
			);
	}

	_makePercentageInfo({ growthString }) {
		const startValue = this._format(this._openingEquityValue);
		const endValue = this._format(this._closingEquityValue);

		return `${startValue} → ${endValue} = ${growthString}`;
	}

	_getEndString({ end }) {
		return formatAsMonthAndYear(end);
	}

	_getValue() {
		return this._closingEquityValue;
	}
}
